<template>
  <div v-if="branding && !isMlsMode" class="branding-overlay">
    <!-- Custom logo (only shown when logo_enabled !== false and logo_url is set) -->
    <div v-if="showLogo" class="branding-logo" :class="logoPositionClass">
      <img :src="branding.logo_url" :alt="branding.company_name || 'Logo'" />
    </div>

    <!-- Copyright text -->
    <div v-if="branding.copyright" class="branding-copyright">
      {{ branding.copyright }}
    </div>

    <!-- Watermark -->
    <div v-if="showWatermark" class="branding-watermark">
      {{ branding.watermark_text }}
    </div>

    <!-- Powered by badge -->
    <div v-if="showPoweredBy" class="powered-by">
      <a href="https://virtualtoureasy.com" target="_blank" rel="noopener">
        <svg class="powered-by-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        Powered by <strong>VirtualTourEasy</strong>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { isMlsPresentationMode, type ViewerPresentationMode } from '../utils/presentationMode'

interface BrandingColors {
  primary?: string
  secondary?: string
  accent?: string
  background?: string
  text?: string
}

interface Branding {
  logo_url?: string
  logo_enabled?: boolean | number
  logo_position?: string  // 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  copyright?: string
  favicon_url?: string
  company_name?: string
  colors?: BrandingColors
  watermark_text?: string
  watermark_enabled?: boolean | number
  powered_by_enabled?: boolean | number
  custom_css?: string
  viewer_font?: string
  gtm_container_id?: string
  ga_measurement_id?: string
  custom_scripts?: string
}

const props = defineProps<{
  branding: Branding | null
  presentationMode?: ViewerPresentationMode
}>()

const isMlsMode = computed(() => isMlsPresentationMode(props.presentationMode || 'default'))

/**
 * True when the viewer is loaded from a white-label customer domain (not a
 * VTE-owned host). Used to gate arbitrary-JS features so one org cannot run
 * scripts under viewer.virtualtoureasy.com and affect other tenants.
 * SSR-safe: returns false when `window` is undefined.
 */
function isCustomDomain(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return false
  if (host.endsWith('.virtualtoureasy.com')) return false
  if (host === 'vt360.co' || host.endsWith('.vt360.co')) return false
  if (host.endsWith('.pages.dev') || host.endsWith('.workers.dev')) return false
  return true
}

const showLogo = computed(() => {
  if (isMlsMode.value) return false
  if (!props.branding?.logo_url) return false
  // logo_enabled defaults to true when not set
  const enabled = props.branding.logo_enabled
  if (enabled === false || enabled === 0) return false
  return true
})

const logoPositionClass = computed(() => {
  const pos = props.branding?.logo_position || 'top-left'
  return `logo-${pos}`
})

const showWatermark = computed(() => {
  if (isMlsMode.value) return false
  if (!props.branding) return false
  const enabled = props.branding.watermark_enabled === true || props.branding.watermark_enabled === 1
  return enabled && !!props.branding.watermark_text?.trim()
})

const showPoweredBy = computed(() => {
  if (isMlsMode.value) return false
  if (!props.branding) return false
  return props.branding.powered_by_enabled === true || props.branding.powered_by_enabled === 1
})

// Apply custom colors as CSS variables
function applyBrandingColors(branding: Branding | null) {
  if (!branding?.colors) return
  const root = document.documentElement
  const colors = branding.colors
  if (colors.primary) root.style.setProperty('--brand-primary', colors.primary)
  if (colors.secondary) root.style.setProperty('--brand-secondary', colors.secondary)
  if (colors.accent) root.style.setProperty('--brand-accent', colors.accent)
  if (colors.background) root.style.setProperty('--brand-background', colors.background)
  if (colors.text) root.style.setProperty('--brand-text', colors.text)
}

// Apply custom font
function applyFont(branding: Branding | null) {
  if (!branding?.viewer_font) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(branding.viewer_font)}&display=swap`
  document.head.appendChild(link)
  document.documentElement.style.setProperty('--brand-font', `'${branding.viewer_font}', system-ui, sans-serif`)
}

// Apply custom favicon
function applyFavicon(branding: Branding | null) {
  if (!branding?.favicon_url) return
  let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = branding.favicon_url
}

// Apply custom CSS
function applyCustomCSS(branding: Branding | null) {
  if (!branding?.custom_css) return
  const style = document.createElement('style')
  style.id = 'custom-branding-css'
  // Remove any existing custom CSS
  document.getElementById('custom-branding-css')?.remove()
  style.textContent = branding.custom_css
  document.head.appendChild(style)
}

// Apply page title
function applyTitle(branding: Branding | null) {
  if (!branding?.company_name) return
  document.title = `${document.title} | ${branding.company_name}`
}

function applyTracking(branding: Branding | null) {
  if (!branding) return

  // Google Tag Manager
  if (branding.gtm_container_id) {
    const id = branding.gtm_container_id.trim()
    if (!document.getElementById(`gtm-${id}`)) {
      // GTM script
      const s = document.createElement('script')
      s.id = `gtm-${id}`
      s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`
      document.head.appendChild(s)
      // GTM noscript (body)
      const ns = document.createElement('noscript')
      ns.id = `gtm-ns-${id}`
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
      document.body.insertAdjacentElement('afterbegin', ns)
    }
  }

  // Google Analytics 4 (GA4) — only if GTM not already handling it
  if (branding.ga_measurement_id && !branding.gtm_container_id) {
    const id = branding.ga_measurement_id.trim()
    if (!document.getElementById(`ga-${id}`)) {
      const s = document.createElement('script')
      s.id = `ga-${id}`
      s.async = true
      s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
      document.head.appendChild(s)
      const i = document.createElement('script')
      i.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`
      document.head.appendChild(i)
    }
  }

  // Custom scripts (arbitrary HTML injected into <head>)
  // ONLY runs on white-label custom domains — the customer's own origin.
  // On VTE-owned domains we refuse to inject: one org's script would otherwise
  // execute under viewer.virtualtoureasy.com and affect visitors of every
  // other tour served from that same origin (cross-tenant XSS vector).
  // Each script is still isolated with try/catch defense-in-depth.
  if (branding.custom_scripts && isCustomDomain()) {
    const existing = document.getElementById('brand-custom-scripts-marker')
    if (!existing) {
      // Drop a marker so we don't inject twice
      const marker = document.createElement('meta')
      marker.id = 'brand-custom-scripts-marker'
      document.head.appendChild(marker)

      const wrapper = document.createElement('div')
      wrapper.innerHTML = branding.custom_scripts

      Array.from(wrapper.querySelectorAll('script')).forEach((oldScript, idx) => {
        try {
          const s = document.createElement('script')
          // Copy all attributes
          Array.from(oldScript.attributes).forEach(a => s.setAttribute(a.name, a.value))

          const isExternal = !!oldScript.src || oldScript.getAttribute('src')

          if (isExternal) {
            // External scripts: always async so they never block viewer rendering
            s.async = true
            s.onerror = () => console.warn(`[VTE branding] Custom script #${idx} failed to load:`, s.src)
          } else {
            // Inline scripts: wrap content in try/catch so a JS error is contained
            const raw = oldScript.textContent || ''
            s.textContent = `try { (function(){\n${raw}\n})(); } catch(e) { console.warn('[VTE branding] Custom script #${idx} error:', e); }`
          }

          document.head.appendChild(s)
        } catch (e) {
          console.warn(`[VTE branding] Failed to inject custom script #${idx}:`, e)
        }
      })
    }
  }
}

function applyBrandingSideEffects(branding: Branding | null) {
  if (isMlsMode.value) return
  applyBrandingColors(branding)
  applyFont(branding)
  applyFavicon(branding)
  applyCustomCSS(branding)
  applyTitle(branding)
  applyTracking(branding)
}

onMounted(() => {
  applyBrandingSideEffects(props.branding)
})

watch(() => [props.branding, props.presentationMode] as const, ([b]) => {
  applyBrandingSideEffects(b)
})
</script>

<style scoped>
.branding-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 15;
  pointer-events: none;
}

.branding-logo {
  position: absolute;
  pointer-events: auto;
  /* Default position: top-left, after the compass */
  top: 14px;
  left: 72px;
  right: auto;
}

/* Logo position variants */
.branding-logo.logo-top-right {
  top: 16px;
  right: 16px;
  left: auto;
  bottom: auto;
}

.branding-logo.logo-top-left {
  top: 14px;
  left: 72px;
  right: auto;
  bottom: auto;
}

.branding-logo.logo-bottom-right {
  bottom: 40px;
  right: 16px;
  top: auto;
  left: auto;
}

.branding-logo.logo-bottom-left {
  bottom: 40px;
  left: 16px;
  top: auto;
  right: auto;
}

.branding-logo img {
  max-height: 40px;
  max-width: 160px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
}

.branding-copyright {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  white-space: nowrap;
  font-family: var(--brand-font, system-ui, sans-serif);
}

.branding-watermark {
  position: absolute;
  bottom: 16px;
  right: 16px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  font-family: var(--brand-font, system-ui, sans-serif);
}

.powered-by {
  position: absolute;
  bottom: 16px;
  left: 16px;
  pointer-events: auto;
}

.powered-by a {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.powered-by a:hover {
  color: rgba(255, 255, 255, 0.7);
}

.powered-by-icon {
  opacity: 0.6;
}

@media (max-width: 480px) {
  .branding-logo img {
    max-height: 28px;
    max-width: 120px;
  }

  .branding-watermark {
    font-size: 9px;
  }
}
</style>
